'use client';

import { cn } from '@workspace/ui/lib/utils';
import { IUITile } from '@/src/types/ITile';
import { useToast } from '@/src/components/ui';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { useProfileUIStore } from '../../../store';
import { createTile, editTile } from '@/src/actions/tiles';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import { getTileEditStatus } from '@/src/utils/helpers';
import { useTilesStore } from '../../../store/tiles-store';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '@workspace/ui/components/button';
import { Icon, BottomToolbar } from '@/src/components/ui';
import { useState } from 'react';

type Props = {
  isMyProfile?: boolean;
  tile: IUITile;
  touchedTileId: string | null;
  setTouchedTileId: (id: string | null) => void;
  onHoverStateChange?: (shouldDisable: boolean) => void;
};

export const NoteTile = ({
  isMyProfile = true,
  onHoverStateChange,
  tile,
  touchedTileId,
  setTouchedTileId,
}: Props) => {
  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);
  const updateTile = useTilesStore((state) => state.updateTile);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const previewMode = useProfileUIStore((state) => state.previewMode);

  const toast = useToast();

  const { onMouseDown, onTouchStart } = useClickOrDrag(() => {
    handleStartEdit();
  });

  const [isEditing, setIsEditing] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: 'Add note...',
      }),
    ],
    content: tile.content || '',
    immediatelyRender: false,
  });

  const handleStartEdit = () => {
    if (previewMode) return;
    updateTile(tile.i, { isEditing: true });
    setIsEditing(true);
    handleStartEditing();
    setTouchedTileId(null);
  };

  const handleSubmitEdit = async () => {
    const html = editor?.getHTML() || '';
    const { wasEdited, hasContent, isEmpty } = getTileEditStatus(
      tile.content || '',
      html
    );

    if (!isEmpty && wasEdited) {
      let response;
      if (!hasContent) {
        toast.updating('Adding note');
        const { iconName, ...tileWithoutIconName } = tile;
        response = await createTile({
          ...tileWithoutIconName,
          content: html,
          title: '',
        });
        if (!response) {
          toast.error('Failed to create note');
          return;
        }
      } else {
        toast.updating('Updating note');
        response = await editTile(
          { content: html, title: '' },
          tile.id as number
        );
        if (!response) {
          toast.error('Failed to update note');
          return;
        }
      }
      updateTile(tile.i, {
        ...tile,
        ...response,
        id: response.id,
        static: false,
      });
    }
    toast.updated('Note saved');
    setIsEditing(false);
    updateTile(tile.i, { isEditing: false });
    handleStopEditing();
  };

  if (isMyProfile && isEditing) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto bg-bg-gray p-4 rounded-t-2xl">
          <EditorContent editor={editor} className="w-full h-full min-h-full" />
        </div>
        <BottomToolbar editor={editor!}>
          <Button
            size="sm"
            onClick={handleSubmitEdit}
            className="p-1.5 w-fit h-fit"
          >
            <Icon name="done" className="md:w-8 md:h-8 w-6 h-6" />
          </Button>
        </BottomToolbar>
      </div>
    );
  }

  if (tile.static && !isEditing) {
    return (
      <div
        className="p-4 md:text-base text-xs font-light h-full w-full text-start"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEdit();
        }}
      >
        Add note...
      </div>
    );
  }

  return (
    <div
      className={cn('p-2 text-xs w-full h-full overflow-y-auto', {
        'md:p-4 md:text-base': !isMobileView,
      })}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('.ProseMirror')) return;
        onMouseDown(e);
      }}
      onTouchStart={(e) => {
        if ((e.target as HTMLElement).closest('.ProseMirror')) return;
        if (touchedTileId !== tile.i) {
          e.preventDefault();
          return;
        }
        onTouchStart(e);
      }}
      dangerouslySetInnerHTML={{ __html: tile.content || '' }}
    />
  );
};
